import { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import Markdown from '@/components/ui/Markdown';

const Container = styled(motion.div)`
    width: 100%;
    position: relative;
    overflow: hidden;

    span[data-action="see-more"] {
        display: inline-block !important;
        color: var(--ant-color-primary, #7543DC) !important;
        font-weight: 600 !important;
        cursor: pointer !important;
        font-size: 13px !important;
        margin-left: 6px;
        text-decoration: none !important;
        user-select: none;
        transition: opacity 0.2s ease, transform 0.1s ease;

        &:hover {
            opacity: 0.8 !important;
            text-decoration: underline !important;
        }

        &:active {
            transform: scale(0.97) !important;
        }
    }
`;

function prepareInlineHtml(html, maxLen, label) {
    if (!html) return { html: '', truncated: false };

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    let textCount = 0;
    let truncated = false;
    let buttonInserted = false;

    function recurse(node) {
        if (truncated) {
            return false;
        }

        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.nodeValue || '';
            if (maxLen !== null && textCount + text.length > maxLen) {
                const remaining = maxLen - textCount;
                node.nodeValue = text.slice(0, remaining) + '...';
                textCount = maxLen;
                truncated = true;

                const btn = doc.createElement('span');
                btn.setAttribute('data-action', 'see-more');
                btn.textContent = ' ' + label;
                node.parentNode.insertBefore(btn, node.nextSibling);
                buttonInserted = true;
            } else {
                textCount += text.length;
            }
            return true;
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
            const children = Array.from(node.childNodes);
            for (const child of children) {
                const keep = recurse(child);
                if (!keep) {
                    node.removeChild(child);
                }
            }
            return true;
        }

        return true;
    }

    recurse(doc.body);

    if (!buttonInserted) {
        let lastNode = null;
        function findLastTextNode(node) {
            if (node.nodeType === Node.TEXT_NODE && (node.nodeValue || '').trim()) {
                lastNode = node;
            } else {
                for (let i = node.childNodes.length - 1; i >= 0; i--) {
                    findLastTextNode(node.childNodes[i]);
                    if (lastNode) break;
                }
            }
        }
        findLastTextNode(doc.body);

        if (lastNode) {
            const btn = doc.createElement('span');
            btn.setAttribute('data-action', 'see-more');
            btn.textContent = ' ' + label;
            lastNode.parentNode.insertBefore(btn, lastNode.nextSibling);
        } else {
            const btn = doc.createElement('span');
            btn.setAttribute('data-action', 'see-more');
            btn.textContent = ' ' + label;
            doc.body.appendChild(btn);
        }
    }

    return {
        html: doc.body.innerHTML,
        truncated
    };
}

/**
 * Renders markdown with an optional line clamp; shows “See more” / “See less” inline with text.
 * Animates expand/shrink smoothly with framer-motion.
 */
export default function ClampedMarkdownDescription({
    source,
    component: MdComponent,
    lineClamp = 5,
    moreLabel = 'See more',
    lessLabel = 'See less',
    stepLineClamps,
    markdownProps,
    resetKey,
}) {
    // Map line clamp steps to character limits:
    // 4 lines -> 250 chars
    // 9 lines -> 650 chars
    // 14 lines -> 1100 chars
    // null -> full text
    const limits = useMemo(() => {
        const clamps = Array.isArray(stepLineClamps) && stepLineClamps.length > 0 ? stepLineClamps : [lineClamp, null];
        return clamps.map((clamp) => {
            if (clamp === null) return null;
            if (clamp <= 4) return 220;
            if (clamp <= 9) return 600;
            return 1100;
        });
    }, [stepLineClamps, lineClamp]);

    const [step, setStep] = useState(0);
    const containerRef = useRef(null);
    const contentRef = useRef(null);
    const [height, setHeight] = useState('auto');
    const [isMeasuring, setIsMeasuring] = useState(true);

    useEffect(() => {
        setStep(0);
        setHeight('auto');
        setIsMeasuring(true);
    }, [resetKey, source]);

    const currentLimit = limits[Math.min(step, limits.length - 1)];
    const isFullyExpanded = currentLimit === null;
    const toggleLabel = !isFullyExpanded ? moreLabel : lessLabel;

    // Convert standard markdown syntax to HTML tags before parsing to count actual text characters
    const htmlSource = useMemo(() => {
        if (!source) return '';
        return source
            .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
            .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
            .replace(/__([^_]+)__/g, '<strong>$1</strong>')
            .replace(/\*([^*]+)\*/g, '<em>$1</em>')
            .replace(/_([^_]+)_/g, '<em>$1</em>');
    }, [source]);

    // Check if the original source actually exceeds the first limit to see if we need a toggle at all
    const hasLongContent = useMemo(() => {
        if (!source) return false;
        const plainText = htmlSource.replace(/<[^>]+>/g, '').trim();
        const firstLimit = limits[0];
        return firstLimit !== null && plainText.length > firstLimit;
    }, [source, htmlSource, limits]);

    const processed = useMemo(() => {
        if (!htmlSource) return { html: '', truncated: false };
        if (!hasLongContent) return { html: htmlSource, truncated: false };

        return prepareInlineHtml(
            htmlSource,
            currentLimit,
            toggleLabel
        );
    }, [htmlSource, currentLimit, toggleLabel, hasLongContent]);

    const handleToggle = () => {
        setStep((prev) => {
            if (prev >= limits.length - 1) {
                return 0;
            }
            return prev + 1;
        });
    };

    const handleContainerClick = (e) => {
        const target = e.target;
        if (target && target.getAttribute('data-action') === 'see-more') {
            e.preventDefault();
            e.stopPropagation();
            handleToggle();
        }
    };

    useLayoutEffect(() => {
        const contentEl = contentRef.current;
        if (!contentEl) return;

        const newHeight = contentEl.offsetHeight;
        setHeight(newHeight);
        setIsMeasuring(false);
    }, [processed.html]);

    // Render plain Markdown if content is short and doesn't need toggle
    if (!hasLongContent) {
        return (
            <div style={{ width: '100%' }}>
                <Markdown source={source} component={MdComponent} {...(markdownProps || {})} />
            </div>
        );
    }

    return (
        <Container
            ref={containerRef}
            onClick={handleContainerClick}
            animate={isMeasuring ? undefined : { height }}
            transition={{ duration: 0.38, ease: [0.25, 0.1, 0.25, 1] }}
        >
            <div ref={contentRef} style={{ width: '100%' }}>
                <Markdown
                    source={processed.html}
                    component={MdComponent}
                    {...(markdownProps || {})}
                />
            </div>
        </Container>
    );
}
